// src/components/WhyChooseUsNewSection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaCheckCircle, 
  FaMapMarkerAlt, 
  FaCrown, 
  FaClock, 
  FaShieldAlt, 
  FaConciergeBell,
  FaLock,
  FaHome
} from 'react-icons/fa';

const WhyChooseUsNewSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Why Choose Us data (client's bullet points)
  const reasons = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Prime Centaurus location",
      description: "Located in Islamabad's most iconic landmark",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <FaCrown />,
      title: "Luxury furnished serviced apartments",
      description: "Premium furnishings and contemporary décor",
      color: "from-amber-500 to-amber-600"
    },
    {
      icon: <FaClock />,
      title: "Ideal for short & long stays",
      description: "Flexible booking options for every need",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <FaShieldAlt />,
      title: "24/7 secure environment",
      description: "Round-the-clock security and surveillance",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <FaConciergeBell />,
      title: "Personalized guest support",
      description: "Dedicated team for all your needs",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: <FaLock />,
      title: "Privacy-focused experience",
      description: "Your privacy is our priority",
      color: "from-red-500 to-red-600"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -30, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 12,
        duration: 0.6 
      }
    },
  };

  return (
    <section className="py-12 bg-white relative overflow-hidden">

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 md:gap-16 gap-8 items-center">
          
          {/* Left Side - Content */}
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-4"
          >
            {/* Section Icon */}
                  <div className="flex items-center gap-3">
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
            <span className="text-[#c9a86c] font-medium tracking-[0.2em] text-xs uppercase">
             Why Choose Us
            </span>
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
          </div>
          

            {/* Heading */}
            <motion.h2 
              variants={itemVariants}
               className="text-4xl lg:text-5xl font-light text-[#1a2b3c] leading-tight "
            >
              Why Choose{' '}
              <span className="font-bold text-[#c9a86c]">Stay IN?</span>
            </motion.h2>

            {/* Description */}
            <motion.p 
              variants={itemVariants}
              className="md:text-[17px] 2xl:text-lg mb-10 text-gray-600 animate-fade-in-up animation-delay-400 leading-relaxed"
            >
              Experience the difference that sets us apart. From our prime location to personalized service, every detail is designed for your comfort.
            </motion.p>

            {/* Reasons List with Check Marks */}
            <motion.div 
              variants={containerVariants}
              className="space-y-4 pt-4"
            >
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4 group"
                >
                  {/* Check Icon */}
                   <div className="w-10 h-10 bg-[#c9a86c]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#c9a86c] transition">
                  <FaCheckCircle className="text-[#c9a86c] text-md group-hover:text-white transition-colors duration-300" />
                </div>
                  {/* <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br ${reason.color} rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
                    <FaCheckCircle className="text-sm" />
                  </div> */}
                  
                  {/* Reason Text */}
                  <div>
                    <h3 className="text-sm 2xl:text-lg font-semibold text-[#1a2b3c] mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-[15px] 2xl:text-lg text-gray-600">{reason.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Closing Statement */}
            <motion.p 
              variants={itemVariants}
             className="md:text-[17px] 2xl:text-lg text-gray-600 animate-fade-in-up animation-delay-400 italic pt-4 border-l-4 border-[#c9a86c] pl-4"
            >
              "Stay IN is more than accommodation — it's a statement of elevated living."
            </motion.p>
          </motion.div>

          {/* Right Side - Visual Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-4"
          >
            {/* Premium Badge */}
            <motion.div 
              variants={itemVariants}
              className="p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 group"
            >
              <div className="flex items-start gap-4 mb-0">
                <div className="w-14 h-14 bg-[#c9a86c]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#c9a86c] transition">
                  <FaHome className="text-[#c9a86c] text-xl group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <h3 className="text-lg 2xl:text-xl font-semibold text-[#1a2b3c]">Premium Living</h3>
                  <p className="md:text-[17px] 2xl:text-lg text-gray-600">Luxury redefined</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-white/10 rounded-xl">
                  <div className="text-xl font-bold text-[#c9a86c]">24/7</div>
                  <div className="text-xs text-gray-600">Security</div>
                </div>
                <div className="text-center p-3 bg-white/10 rounded-xl">
                  <div className="text-xl font-bold text-[#c9a86c]">100%</div>
                  <div className="text-xs text-gray-600">Privacy</div>
                </div>
              </div>
            </motion.div>

            {/* Feature Highlights */}
            <motion.div 
              variants={itemVariants}
              className="p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 group"
            >
              <h4 className="text-lg 2xl:text-xl font-semibold text-[#1a2b3c] mb-4">Quick Highlights</h4>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">Prime Centaurus location</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">Luxury furnished apartments</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">Ideal for short & long stays</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">24/7 secure environment</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">Personalized guest support</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
                  <span className="text-[15px] 2xl:text-lg text-gray-600">Privacy-focused experience</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsNewSection;